import { forwardRef, useEffect, useImperativeHandle, useRef, type RefObject } from "react";
import { SUBTLE_RING, type DotGridColorTheme, type RippleRing, type DotGridCanvasHandle } from "@/components/backgrounds/dot-grid-types";

/**
 * Animated dot grid background with ripple effects.
 *
 * Renders a Canvas 2D dot grid that fades in, then plays ripples
 * emanating from the center of `rippleOriginRef` (or panel center as fallback).
 *
 * Ripple schedule (all optional via props):
 *   1. Initial two-ring ripple on mount (600ms delay)
 *   2. Follow-up single-ring ripple at 50% intensity after 3 minutes
 *   3. Hover over `rippleOriginRef` triggers a single-ring ripple (50%, 10s cooldown)
 *
 * Exposes an imperative `triggerRipple(rings)` method via ref for external callers.
 *
 * Colors adapt to light/dark mode via CSS custom properties on :root.
 */

const DOT_RADIUS = 2.25;
const DOT_SPACING = 20;
const GRID_OPACITY = 0.7;

const RIPPLE_DELAY_MS = 600;
const RIPPLE_RING_WIDTH = 350;
const DOT_FLASH_DURATION_MS = 1500;
const FOLLOWUP_DELAY_MS = 3 * 60 * 1000;
const HOVER_COOLDOWN_MS = 10_000;
const INITIAL_RINGS: RippleRing[] = [
    { delay: 0, duration: 6000, intensity: 0.65 },
    { delay: 900, duration: 7500, intensity: 0.35 },
];

type RippleEvent = {
    startTime: number;
    rings: RippleRing[];
};

function readRgbVar(name: string): [number, number, number] {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const parts = raw.split(/\s+/).map(Number);
    if (parts.length >= 3) return [parts[0], parts[1], parts[2]];
    return [200, 200, 200];
}

function isDarkMode(): boolean {
    return document.documentElement.classList.contains("dark");
}



function getColors(): DotGridColorTheme {
    if (isDarkMode()) {
        return {
            base: readRgbVar("--ona-gray-700"),
            bright: readRgbVar("--ona-blue-200"),
        };
    }
    return {
        base: readRgbVar("--ona-gray-200"),
        bright: readRgbVar("--ona-blue-200"),
    };
}

function easeOutQuad(t: number): number {
    return 1 - (1 - t) * (1 - t);
}

function flashCurve(t: number): number {
    const s = Math.sin(t * Math.PI);
    return s * s;
}

type DotGridCanvasProps = {
    rippleOriginRef?: RefObject<HTMLElement | null>;
    /** Enable hover-triggered ripples on rippleOriginRef. Default: true */
    enableHoverRipple?: boolean;
    /** Enable the timed follow-up ripple (3min after initial). Default: true */
    enableFollowUpRipple?: boolean;
    /** Override dot colors (e.g. per-step onboarding themes). Falls back to CSS vars when absent. */
    colorTheme?: DotGridColorTheme;
};

export const DotGridCanvas = forwardRef<DotGridCanvasHandle, DotGridCanvasProps>(
    ({ rippleOriginRef, enableHoverRipple = true, enableFollowUpRipple = true, colorTheme }, ref) => {
        const canvasRef = useRef<HTMLCanvasElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);
        const triggerRippleFnRef = useRef<((rings: RippleRing[]) => void) | null>(null);
        const colorThemeRef = useRef(colorTheme);
        colorThemeRef.current = colorTheme;
        const updateColorsFnRef = useRef<(() => void) | null>(null);

        useImperativeHandle(ref, () => ({
            triggerRipple: (rings: RippleRing[]) => {
                triggerRippleFnRef.current?.(rings);
            },
        }));

        useEffect(() => {
            const canvas = canvasRef.current;
            const container = containerRef.current;
            if (!canvas || !container) return;

            const ctx = canvas.getContext("2d");
            if (!ctx) return;

            let colors = colorThemeRef.current ?? getColors();

            updateColorsFnRef.current = () => {
                colors = colorThemeRef.current ?? getColors();
                if (!animating) drawStatic();
            };
            let animFrameId: number | null = null;

            const activeRipples: RippleEvent[] = [];
            let animating = false;

            let dots: { x: number; y: number }[] = [];
            let originX = 0;
            let originY = 0;
            let maxDist = 0;
            let w = 0;
            let h = 0;

            function sizeCanvas() {
                const dpr = window.devicePixelRatio || 1;
                const rect = container!.getBoundingClientRect();
                w = rect.width;
                h = rect.height;

                canvas!.width = w * dpr;
                canvas!.height = h * dpr;
                canvas!.style.width = `${w}px`;
                canvas!.style.height = `${h}px`;
                ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

                const cols = Math.ceil(w / DOT_SPACING) + 1;
                const rows = Math.ceil(h / DOT_SPACING) + 1;
                const gridW = (cols - 1) * DOT_SPACING;
                const gridH = (rows - 1) * DOT_SPACING;
                const offsetX = (w - gridW) / 2;
                const offsetY = (h - gridH) / 2;

                dots = [];
                for (let row = 0; row < rows; row++) {
                    for (let col = 0; col < cols; col++) {
                        dots.push({
                            x: offsetX + col * DOT_SPACING,
                            y: offsetY + row * DOT_SPACING,
                        });
                    }
                }

                originX = w / 2;
                originY = h / 2;
                if (rippleOriginRef?.current) {
                    const originRect = rippleOriginRef.current.getBoundingClientRect();
                    const containerRect = container!.getBoundingClientRect();
                    originX = originRect.left + originRect.width / 2 - containerRect.left;
                    originY = originRect.top + originRect.height / 2 - containerRect.top;
                }

                maxDist = dots.reduce(
                    (m, d) => Math.max(m, Math.hypot(d.x - originX, d.y - originY)),
                    Math.hypot(w, h),
                );
            }

            function drawDots(getBrightness: (dot: { x: number; y: number }) => number) {
                ctx!.clearRect(0, 0, w, h);
                for (const dot of dots) {
                    const brightness = getBrightness(dot);
                    const r = colors.base[0] + (colors.bright[0] - colors.base[0]) * brightness;
                    const g = colors.base[1] + (colors.bright[1] - colors.base[1]) * brightness;
                    const b = colors.base[2] + (colors.bright[2] - colors.base[2]) * brightness;
                    ctx!.beginPath();
                    ctx!.arc(dot.x, dot.y, DOT_RADIUS, 0, Math.PI * 2);
                    ctx!.fillStyle = `rgb(${r}, ${g}, ${b})`;
                    ctx!.fill();
                }
            }

            function drawStatic() {
                drawDots(() => 0);
            }

            function triggerRipple(rings: RippleRing[]) {
                if (rippleOriginRef?.current && container) {
                    const originRect = rippleOriginRef.current.getBoundingClientRect();
                    const containerRect = container.getBoundingClientRect();
                    originX = originRect.left + originRect.width / 2 - containerRect.left;
                    originY = originRect.top + originRect.height / 2 - containerRect.top;
                    maxDist = dots.reduce(
                        (m, d) => Math.max(m, Math.hypot(d.x - originX, d.y - originY)),
                        Math.hypot(w, h),
                    );
                }

                activeRipples.push({ startTime: performance.now(), rings });
                if (!animating) {
                    animating = true;
                    animFrameId = requestAnimationFrame(renderFrame);
                }
            }

            triggerRippleFnRef.current = triggerRipple;

            function renderFrame(now: number) {
                const totalReach = maxDist + RIPPLE_RING_WIDTH;
                let anyActive = false;

                drawDots((dot) => {
                    const dist = Math.hypot(dot.x - originX, dot.y - originY);
                    let brightness = 0;

                    for (const ripple of activeRipples) {
                        const rippleElapsed = now - ripple.startTime;

                        for (const ring of ripple.rings) {
                            const ringElapsed = rippleElapsed - ring.delay;
                            if (ringElapsed < 0) {
                                anyActive = true;
                                continue;
                            }

                            const progress = Math.min(ringElapsed / ring.duration, 1);
                            const radius = easeOutQuad(progress) * totalReach;
                            const ringStart = radius - RIPPLE_RING_WIDTH;

                            if (dist >= ringStart && dist <= radius) {
                                const posInRing = 1 - (radius - dist) / RIPPLE_RING_WIDTH;
                                brightness = Math.max(brightness, flashCurve(posInRing) * ring.intensity);
                                anyActive = true;
                            } else if (dist < ringStart) {
                                const passRatio = dist / totalReach;
                                const passTime = ring.delay + passRatio * ring.duration;
                                const timeSincePass = rippleElapsed - passTime;
                                if (timeSincePass > 0 && timeSincePass < DOT_FLASH_DURATION_MS) {
                                    const trailBrightness =
                                        flashCurve(1 - timeSincePass / DOT_FLASH_DURATION_MS) * ring.intensity * 0.25;
                                    brightness = Math.max(brightness, trailBrightness);
                                    anyActive = true;
                                }
                            } else if (progress < 1) {
                                anyActive = true;
                            }
                        }
                    }

                    return brightness;
                });

                if (anyActive) {
                    animFrameId = requestAnimationFrame(renderFrame);
                } else {
                    activeRipples.length = 0;
                    animating = false;
                    drawStatic();
                }
            }

            sizeCanvas();
            drawStatic();

            const timers: ReturnType<typeof setTimeout>[] = [];

            timers.push(
                setTimeout(() => {
                    triggerRipple(INITIAL_RINGS);
                }, RIPPLE_DELAY_MS),
            );

            if (enableFollowUpRipple) {
                timers.push(
                    setTimeout(() => {
                        triggerRipple(SUBTLE_RING);
                    }, RIPPLE_DELAY_MS + FOLLOWUP_DELAY_MS),
                );
            }

            let lastHoverRippleTime = 0;
            function onQuoteMouseEnter() {
                const now = performance.now();
                if (now - lastHoverRippleTime < HOVER_COOLDOWN_MS) return;
                lastHoverRippleTime = now;
                triggerRipple(SUBTLE_RING);
            }

            const hoverTarget = enableHoverRipple ? rippleOriginRef?.current : null;
            if (hoverTarget) {
                hoverTarget.addEventListener("mouseenter", onQuoteMouseEnter);
            }

            const ro = new ResizeObserver(() => {
                sizeCanvas();
                if (!animating) {
                    drawStatic();
                }
            });
            ro.observe(container);

            const mo = new MutationObserver(() => {
                colors = colorThemeRef.current ?? getColors();
                if (!animating) drawStatic();
            });
            mo.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });

            return () => {
                triggerRippleFnRef.current = null;
                updateColorsFnRef.current = null;
                for (const t of timers) clearTimeout(t);
                if (animFrameId !== null) cancelAnimationFrame(animFrameId);
                if (hoverTarget) {
                    hoverTarget.removeEventListener("mouseenter", onQuoteMouseEnter);
                }
                ro.disconnect();
                mo.disconnect();
            };
        }, [rippleOriginRef, enableHoverRipple, enableFollowUpRipple]);

        useEffect(() => {
            updateColorsFnRef.current?.();
        }, [colorTheme]);

        return (
            <>
                <style>{`@keyframes dot-grid-fade-in { from { opacity: 0 } to { opacity: ${GRID_OPACITY} } }`}</style>
                <div
                    ref={containerRef}
                    className="pointer-events-none absolute inset-0 h-full w-full select-none"
                    style={{
                        maskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                        WebkitMaskImage: "linear-gradient(to right, transparent, black 10%, black 90%, transparent)",
                    }}
                >
                    <div
                        className="h-full w-full"
                        style={{
                            maskImage: "radial-gradient(100% 100%, transparent 0%, black 50%)",
                            WebkitMaskImage: "radial-gradient(100% 100%, transparent 0%, black 50%)",
                            animation: "dot-grid-fade-in 1.5s ease-out forwards",
                        }}
                    >
                        <canvas ref={canvasRef} className="block h-full w-full" />
                    </div>
                </div>
            </>
        );
    },
);

DotGridCanvas.displayName = "DotGridCanvas";
